import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of, from } from 'rxjs';
import { retry, catchError, mergeMap } from 'rxjs/operators';
import { environment } from '../environments/environmentCAS';
import { ApiMapperUtil } from '../utils/api-mapper.util';

export interface ApicilBeneficiaire {
  role: 'ASSUR' | 'CONJT' | 'ENFNT' | 'AYDT';
  nom?: string;
  prenom?: string;
  dateNaissance: string;
  regimeSocial: 'GENERAL' | 'ALSACEMOSELLE' | 'SSI';
}

export interface ApicilTarificationRequest {
  typePartenaire: 'COURTAGE';
  codePartenaire: string;
  codeProduit: 'ApiSante';
  codePostalAssure: string;
  dateEffetContrat: string;
  situationProfessionnelle?: 'ACTIF' | 'CONJCOL' | 'ETUDIANT' | 'INACTIF' | 'NOCTR' | 'RETAITE' | 'SALARIE';
  codesAvantages?: string;
  beneficiaires: ApicilBeneficiaire[];
}

export interface ApicilPrestationConfort {
  LibellePrestationDeConfort: string;
  TarifPrestationDeConfort: string;
}

export interface ApicilFormule {
  LibelleFormule: string;
  TarifFormule: string;
  PrestationsDeConfort: ApicilPrestationConfort[];
}

export interface ApicilTarificationResponse {
  Catalogues?: {
    CodeProduit: string;
  };
  Formules: ApicilFormule[];
}

@Injectable({
  providedIn: 'root'
})
export class ApicilService {
  private readonly baseUrl = `${environment.casApiUrl}${environment.endpoints.apicil.tarification}`;
  private readonly devisUrl = `${environment.casApiUrl}${environment.endpoints.apicil.creationDevis}`;
  private readonly souscriptionUrl = `${environment.casApiUrl}${environment.endpoints.apicil.demarrerSouscription}`;

  constructor(private http: HttpClient) {}

  getTarification(request: ApicilTarificationRequest): Observable<ApicilTarificationResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post<ApicilTarificationResponse>(this.baseUrl, request, { headers }).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400 && error.error) {
          return of(error.error as ApicilTarificationResponse);
        }
        return throwError(() => error);
      })
    );
  }

  createDevis(request: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(this.devisUrl, request, { headers }).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  demarrerSouscription(referenceDevis: string, request: any): Observable<any> {
    const url = `${this.souscriptionUrl}/${referenceDevis}/demande`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(url, request, { headers }).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getReferentielPays(): Observable<any> {
    const url = `${environment.casApiUrl}${environment.endpoints.apicil.referentielPays}`;
    return this.http.get(url).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getReferentielActivites(): Observable<any> {
    const url = `${environment.casApiUrl}${environment.endpoints.apicil.referentielActivites}`;
    return this.http.get(url).pipe(
      retry(1),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  getLiasseDevis(idOpportunite: string): Observable<any> {
    const url = `${environment.casApiUrl}${environment.endpoints.apicil.telechargementLiasse}/${idOpportunite}/liasse`;
    return this.http.get(url).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  downloadLiasseDevis(idOpportunite: string): Observable<Blob> {
    const url = `${environment.casApiUrl}${environment.endpoints.apicil.telechargementLiasse}/${idOpportunite}/liasse/download`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.error instanceof Blob) {
          return from(error.error.text()).pipe(
            mergeMap((rawText: string) => {
              let message = rawText || 'Erreur téléchargement liasse APICIL.';
              try {
                const parsed = JSON.parse(rawText);
                message =
                  parsed?.detail ||
                  parsed?.message ||
                  parsed?.error?.errorDescription ||
                  message;
              } catch {
                // Keep raw text as fallback.
              }
              return throwError(() => new Error(message));
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  static mapRegimeSocial(regime: string): 'GENERAL' | 'ALSACEMOSELLE' | 'SSI' {
    return ApiMapperUtil.getApicilRegime(regime) as 'GENERAL' | 'ALSACEMOSELLE' | 'SSI';
  }
}
